import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Typography, 
  Container, 
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  CircularProgress
} from '@mui/material';
import OfflineMode from '../components/OfflineMode';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { AuthContext } from '../context/AuthContext';
import authService from '../services/authService';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import InfoIcon from '@mui/icons-material/Info';
import TitleIcon from '@mui/icons-material/Title';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import GavelIcon from '@mui/icons-material/Gavel';
import SchoolIcon from '@mui/icons-material/School';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ScienceIcon from '@mui/icons-material/Science';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import { resetOnboarding } from '../components/OnboardingModal';
import axios from 'axios';
import { getBackendBaseUrl } from '../utils/configUtils';
import { areHintsEnabled, setHintsEnabled } from '../components/InfoIconButton';
import letGoService from '../services/letGoService';

// Create admin API instance with /api/admin base path
const adminApi = axios.create({
  baseURL: `${getBackendBaseUrl()}/api/admin`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// Add auth token to admin requests
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('cubxly_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const Settings = () => {
  const navigate = useAppNavigation();
  const { logout, currentUser } = useContext(AuthContext);
  const [darkMode, setDarkMode] = useState(false);
  const [notifications, setNotifications] = useState(true);
  const [useDefaultItemNames, setUseDefaultItemNames] = useState(false);
  const [useSimpleHome, setUseSimpleHome] = useState(() => {
    const saved = localStorage.getItem('clutterlogix_home_mode');
    return saved ? saved === 'simple' : true;
  });
  const [showHints, setShowHints] = useState(areHintsEnabled());
  const [letGoEmailReminders, setLetGoEmailReminders] = useState(true);
  const [weeklyDeclutterScan, setWeeklyDeclutterScan] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('info');
  
  // Delete account state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Admin Let Go test triggers state
  const [letGoTestStatus, setLetGoTestStatus] = useState(null);
  const [letGoTestLoading, setLetGoTestLoading] = useState(false);
  
  // Load saved preferences on component mount
  useEffect(() => {
    const savedNamePreference = localStorage.getItem('useDefaultItemNames');
    if (savedNamePreference !== null) {
      setUseDefaultItemNames(savedNamePreference === 'true');
    }
    
    // Load Let Go preferences
    const loadLetGoPreferences = async () => {
      try {
        const prefs = await letGoService.getPreferences();
        setLetGoEmailReminders(prefs.let_go_email_reminders !== false);
        
        // Load weekly scan settings
        const scanSettings = await letGoService.getWeeklyScanSettings();
        setWeeklyDeclutterScan(scanSettings.weekly_declutter_scan || false);
      } catch (err) {
        console.error('Error loading Let Go preferences:', err);
      }
    };
    loadLetGoPreferences();
  }, []);
  
  // Handle dark mode toggle
  const handleDarkModeToggle = () => {
    setDarkMode(!darkMode);
    setAlertMessage('Theme preference saved! This will be implemented in a future update.');
    setShowAlert(true);
  };
  
  // Handle notifications toggle
  const handleNotificationsToggle = () => {
    setNotifications(!notifications);
    setAlertMessage('Notification preference saved! This will be implemented in a future update.');
    setShowAlert(true);
  };
  
  // Handle Let Go email reminders toggle
  const handleLetGoEmailRemindersToggle = async () => {
    const newValue = !letGoEmailReminders;
    setLetGoEmailReminders(newValue);
    try {
      await letGoService.updatePreferences({ let_go_email_reminders: newValue });
      setAlertMessage(newValue ? 'You will receive donation reminders after 30 days' : 'Donation reminders disabled');
      setAlertSeverity('success');
    } catch (err) {
      console.error('Error updating Let Go preferences:', err);
      setLetGoEmailReminders(!newValue); // Revert on error
      setAlertMessage('Failed to update preference');
      setAlertSeverity('error');
    }
    setShowAlert(true);
  };
  
  // Handle weekly declutter scan toggle
  const handleWeeklyDeclutterScanToggle = async () => {
    const newValue = !weeklyDeclutterScan;
    setWeeklyDeclutterScan(newValue);
    try {
      await letGoService.updateWeeklyScanSettings(newValue);
      setAlertMessage(newValue ? 'Weekly AI declutter scan enabled' : 'Weekly AI declutter scan disabled');
      setAlertSeverity('success');
    } catch (err) {
      console.error('Error updating weekly scan settings:', err);
      setWeeklyDeclutterScan(!newValue); // Revert on error
      setAlertMessage('Failed to update preference');
      setAlertSeverity('error');
    }
    setShowAlert(true);
  };
  
  // Offline mode is now handled by the OfflineMode component
  
  // Handle home mode toggle
  const handleHomeModeToggle = () => {
    const newValue = !useSimpleHome;
    setUseSimpleHome(newValue);
    localStorage.setItem('clutterlogix_home_mode', newValue ? 'simple' : 'legacy');
    setAlertMessage(newValue ? 'Switched to Simple Home - big buttons for quick access' : 'Switched to Full Dashboard - all features visible');
    setAlertSeverity('success');
    setShowAlert(true);
  };
  
  // Handle default item names toggle
  const handleDefaultItemNamesToggle = () => {
    const newValue = !useDefaultItemNames;
    setUseDefaultItemNames(newValue);
    localStorage.setItem('useDefaultItemNames', newValue.toString());
    setAlertMessage(`Item names will ${newValue ? 'now use default names when empty' : 'be left empty by default'}`);
    setShowAlert(true);
  };
  
  // Handle clear cache
  const handleClearCache = () => {
    setAlertMessage('Cache cleared! This will be implemented in a future update.');
    setShowAlert(true);
  };
  
  // Handle about app
  const handleAboutApp = () => {
    setAlertMessage('Cubxly Mobile v0.1.0 - Home Inventory Management');
    setAlertSeverity('info');
    setShowAlert(true);
  };
  
  // ============ ADMIN LET GO TEST TRIGGERS ============
  
  // Fetch Let Go test status
  const fetchLetGoTestStatus = async () => {
    if (!currentUser?.is_admin) return;
    try {
      setLetGoTestLoading(true);
      const response = await adminApi.get('/let-go/test/status');
      setLetGoTestStatus(response.data);
    } catch (err) {
      console.error('Error fetching Let Go test status:', err);
    } finally {
      setLetGoTestLoading(false);
    }
  };
  
  // Age random items for testing
  const handleAgeItems = async (count = 10) => {
    try {
      setLetGoTestLoading(true);
      const response = await adminApi.post('/let-go/test/age-items', { count });
      setAlertMessage(response.data.message);
      setAlertSeverity('success');
      setShowAlert(true);
      fetchLetGoTestStatus();
    } catch (err) {
      setAlertMessage('Failed to age items: ' + (err.response?.data?.error || err.message));
      setAlertSeverity('error');
      setShowAlert(true);
    } finally {
      setLetGoTestLoading(false);
    }
  };
  
  // Clear all Let Go statuses
  const handleClearLetGoStatuses = async () => {
    try {
      setLetGoTestLoading(true);
      const response = await adminApi.post('/let-go/test/clear-all');
      setAlertMessage(response.data.message);
      setAlertSeverity('success');
      setShowAlert(true);
      fetchLetGoTestStatus();
    } catch (err) {
      setAlertMessage('Failed to clear statuses: ' + (err.response?.data?.error || err.message));
      setAlertSeverity('error');
      setShowAlert(true);
    } finally {
      setLetGoTestLoading(false);
    }
  };
  
  // Create test notification
  const handleCreateTestNotification = async () => {
    try {
      setLetGoTestLoading(true);
      const response = await adminApi.post('/notifications/create-test', {
        type: 'exit_bin_reminder',
        title: 'Test: Items ready to donate',
        message: 'This is a test notification. You have items in your Exit Bin ready to donate!',
        action_url: '/exit-bin'
      });
      setAlertMessage('Test notification created! Check the bell icon.');
      setAlertSeverity('success');
      setShowAlert(true);
    } catch (err) {
      setAlertMessage('Failed to create notification: ' + (err.response?.data?.error || err.message));
      setAlertSeverity('error');
      setShowAlert(true);
    } finally {
      setLetGoTestLoading(false);
    }
  };
  
  // Process exit bin reminders
  const handleProcessReminders = async () => {
    try {
      setLetGoTestLoading(true);
      const response = await adminApi.post('/notifications/process-reminders');
      setAlertMessage(response.data.message);
      setAlertSeverity('success');
      setShowAlert(true);
    } catch (err) {
      setAlertMessage('Failed to process reminders: ' + (err.response?.data?.error || err.message));
      setAlertSeverity('error');
      setShowAlert(true);
    } finally {
      setLetGoTestLoading(false);
    }
  };
  
  // Send test email
  const handleSendTestEmail = async () => {
    try {
      setLetGoTestLoading(true);
      const response = await adminApi.post('/notifications/send-test-email');
      setAlertMessage(response.data.message);
      setAlertSeverity('success');
      setShowAlert(true);
    } catch (err) {
      setAlertMessage('Failed to send test email: ' + (err.response?.data?.error || err.message));
      setAlertSeverity('error');
      setShowAlert(true);
    } finally {
      setLetGoTestLoading(false);
    }
  };
  
  // Load test status on mount for admins
  useEffect(() => {
    if (currentUser?.is_admin) {
      fetchLetGoTestStatus();
    }
  }, [currentUser]);
  
  // Handle delete account
  const handleDeleteAccountClick = () => {
    setDeleteDialogOpen(true);
    setDeleteConfirmText('');
  };
  
  const handleDeleteAccountConfirm = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setAlertMessage('Please type DELETE to confirm account deletion');
      setAlertSeverity('warning');
      setShowAlert(true);
      return;
    }
    
    setIsDeleting(true);
    try {
      const result = await authService.deleteAccount();
      
      if (result.success) {
        setDeleteDialogOpen(false);
        setAlertMessage('Your account and all data have been permanently deleted.');
        setAlertSeverity('success');
        setShowAlert(true);
        
        // Log out and redirect to login after a short delay
        setTimeout(() => {
          logout();
          navigate.to.LOGIN();
        }, 2000);
      } else {
        setAlertMessage(result.error || 'Failed to delete account. Please try again.');
        setAlertSeverity('error');
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      setAlertMessage('An error occurred while deleting your account. Please try again.');
      setAlertSeverity('error');
      setShowAlert(true);
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleDeleteDialogClose = () => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
    }
  };
  
  // Handle reset onboarding (for testing)
  const handleResetOnboarding = () => {
    resetOnboarding();
    setAlertMessage('Onboarding reset! Refresh the page to see the tutorial again.');
    setShowAlert(true);
  };
  
  // Handle show hints toggle
  const handleShowHintsToggle = () => {
    const newValue = !showHints;
    setShowHints(newValue);
    setHintsEnabled(newValue);
    setAlertMessage(newValue ? 'Help hints enabled - tap (i) icons for tips' : 'Help hints hidden');
    setShowAlert(true);
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="back"
            onClick={() => navigate.to.HOME()}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Settings
          </Typography>
        </Toolbar>
      </AppBar>
      
      {/* Main Content */}
      <Container component="main" sx={{ flexGrow: 1, py: 3 }}>
        {showAlert && (
          <Alert 
            severity={alertSeverity} 
            sx={{ mb: 2 }}
            onClose={() => setShowAlert(false)}
          >
            {alertMessage}
          </Alert>
        )}
        
        <Paper sx={{ p: 0 }}>
          <List>
            {/* Appearance */}
            <ListItem>
              <ListItemIcon>
                <DarkModeIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Dark Mode" 
                secondary="Use dark theme throughout the app"
              />
              <Switch
                edge="end"
                checked={darkMode}
                onChange={handleDarkModeToggle}
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            {/* Notifications */}
            <ListItem>
              <ListItemIcon>
                <NotificationsIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Notifications" 
                secondary="Receive alerts and reminders"
              />
              <Switch
                edge="end"
                checked={notifications}
                onChange={handleNotificationsToggle}
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            {/* Let Go Email Reminders */}
            <ListItem>
              <ListItemIcon>
                <VolunteerActivismIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Donation Reminders" 
                secondary="Get email reminders for items in Exit Bin after 30 days"
              />
              <Switch
                edge="end"
                checked={letGoEmailReminders}
                onChange={handleLetGoEmailRemindersToggle}
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            {/* Weekly Declutter Scan */}
            <ListItem>
              <ListItemIcon>
                <VolunteerActivismIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Weekly AI Declutter Scan" 
                secondary="Automatically prompt to review items to let go each week"
              />
              <Switch
                edge="end"
                checked={weeklyDeclutterScan}
                onChange={handleWeeklyDeclutterScanToggle}
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            {/* Subscription Management */}
            <ListItem button onClick={() => navigate.to.SUBSCRIPTION()}>
              <ListItemIcon>
                <CreditCardIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Subscription" 
                secondary="Manage your plan, billing, and usage"
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            {/* OfflineMode component - replaces the old simple toggle */}
            <OfflineMode />
            
            <Divider variant="inset" component="li" />
            
            {/* Home Page Mode */}
            <ListItem>
              <ListItemIcon>
                <ViewCompactIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Simple Home Page" 
                secondary="Use streamlined 3-button home for quick access (great for beginners)"
              />
              <Switch
                edge="end"
                checked={useSimpleHome}
                onChange={handleHomeModeToggle}
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            {/* Default Item Names */}
            <ListItem>
              <ListItemIcon>
                <TitleIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Default Item Names" 
                secondary="Automatically generate default names for new items instead of leaving them empty"
              />
              <Switch
                edge="end"
                checked={useDefaultItemNames}
                onChange={handleDefaultItemNamesToggle}
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            {/* Clear Cache */}
            <ListItem button onClick={handleClearCache}>
              <ListItemIcon>
                <DeleteSweepIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Clear Cache" 
                secondary="Delete temporary files from device"
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            {/* Legal Documents */}
            <ListItem>
              <ListItemIcon>
                <GavelIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Legal Documents" 
                secondary="Terms of Service and Privacy Policy"
              />
            </ListItem>
            
            <Box sx={{ pl: 4, pr: 2, pb: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                  component="button"
                  onClick={() => navigate.to.TERMS_OF_SERVICE()}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  Terms of Service
                </Box>
                <Box
                  component="button"
                  onClick={() => navigate.to.PRIVACY_POLICY()}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  Privacy Policy
                </Box>
                <Box
                  component="button"
                  onClick={() => navigate.to.DATA_DELETION()}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  Data Deletion Instructions
                </Box>
              </Box>
            </Box>
            
            <Divider variant="inset" component="li" />
            
            {/* Show Help Hints */}
            <ListItem>
              <ListItemIcon>
                <HelpOutlineIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Show Help Hints" 
                secondary="Display (i) icons with tips next to buttons"
              />
              <Switch
                edge="end"
                checked={showHints}
                onChange={handleShowHintsToggle}
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            {/* Reset Onboarding (Dev/Testing) */}
            <ListItem button onClick={handleResetOnboarding}>
              <ListItemIcon>
                <SchoolIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Reset Onboarding" 
                secondary="Show the getting started tutorial again"
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            {/* About */}
            <ListItem button onClick={handleAboutApp}>
              <ListItemIcon>
                <InfoIcon />
              </ListItemIcon>
              <ListItemText 
                primary="About" 
                secondary="App version and information"
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            {/* Delete Account - Danger Zone */}
            <ListItem 
              button 
              onClick={handleDeleteAccountClick}
              sx={{ 
                '&:hover': { 
                  backgroundColor: 'rgba(211, 47, 47, 0.08)' 
                } 
              }}
            >
              <ListItemIcon>
                <DeleteForeverIcon color="error" />
              </ListItemIcon>
              <ListItemText 
                primary={<Typography color="error">Delete Account</Typography>}
                secondary="Permanently delete your account and all data"
              />
            </ListItem>
          </List>
        </Paper>
        
        {/* Admin Let Go Test Triggers - Only visible to admins */}
        {currentUser?.is_admin && (
          <Paper sx={{ mt: 3, borderRadius: 2, border: '2px solid', borderColor: 'warning.main' }}>
            <Box sx={{ p: 2, backgroundColor: 'warning.light', borderRadius: '8px 8px 0 0' }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScienceIcon /> Let Go Feature - Test Triggers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Admin tools to simulate conditions for testing the Let Go feature
              </Typography>
            </Box>
            
            <Box sx={{ p: 2 }}>
              {/* Status Display */}
              {letGoTestStatus && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Current Status:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="body2">
                      <strong>Total Items:</strong> {letGoTestStatus.total_items}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Suggestion Eligible:</strong> {letGoTestStatus.suggestion_eligible}
                    </Typography>
                    <Typography variant="body2">
                      <strong>In Exit Bin:</strong> {letGoTestStatus.exit_bin_count}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Marked Keep:</strong> {letGoTestStatus.keep_count}
                    </Typography>
                  </Box>
                </Box>
              )}
              
              {/* Action Buttons */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={letGoTestLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                  onClick={() => handleAgeItems(10)}
                  disabled={letGoTestLoading}
                >
                  Age 10 Items (13 months)
                </Button>
                
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={letGoTestLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                  onClick={() => handleAgeItems(20)}
                  disabled={letGoTestLoading}
                >
                  Age 20 Items
                </Button>
                
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={letGoTestLoading ? <CircularProgress size={20} color="inherit" /> : <RestartAltIcon />}
                  onClick={handleClearLetGoStatuses}
                  disabled={letGoTestLoading}
                >
                  Reset All Let Go Data
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<VolunteerActivismIcon />}
                  onClick={() => navigate.to.LET_GO()}
                >
                  Go to Let Go Page
                </Button>
              </Box>
              
              {/* Notification Test Section */}
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                Notification Testing:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Button
                  variant="contained"
                  color="info"
                  startIcon={letGoTestLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                  onClick={handleCreateTestNotification}
                  disabled={letGoTestLoading}
                >
                  Create Test Notification
                </Button>
                
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={letGoTestLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                  onClick={handleProcessReminders}
                  disabled={letGoTestLoading}
                >
                  Process 30-Day Reminders
                </Button>
                
                <Button
                  variant="contained"
                  color="success"
                  startIcon={letGoTestLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                  onClick={handleSendTestEmail}
                  disabled={letGoTestLoading}
                >
                  Send Test Email
                </Button>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                "Age Items" sets last_searched_at to 13 months ago, making items appear in Let Go suggestions.
                "Reset All" clears exit_bin_status and last_searched_at for all your items.
                "Create Test Notification" adds a test notification to the bell icon.
                "Process 30-Day Reminders" sends notifications for items in Exit Bin 30+ days.
              </Typography>
            </Box>
          </Paper>
        )}
      </Container>
      
      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-account-dialog-title"
        aria-describedby="delete-account-dialog-description"
      >
        <DialogTitle id="delete-account-dialog-title" sx={{ color: 'error.main' }}>
          ⚠️ Delete Account Permanently
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-account-dialog-description" sx={{ mb: 2 }}>
            <strong>This action cannot be undone.</strong> This will permanently delete:
          </DialogContentText>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>All your items and their images</li>
            <li>All your boxes</li>
            <li>All chat history</li>
            <li>All reports and exports</li>
            <li>Your account and profile</li>
          </Box>
          <DialogContentText sx={{ mb: 2 }}>
            To confirm, please type <strong>DELETE</strong> below:
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="Type DELETE to confirm"
            disabled={isDeleting}
            error={deleteConfirmText.length > 0 && deleteConfirmText !== 'DELETE'}
            helperText={deleteConfirmText.length > 0 && deleteConfirmText !== 'DELETE' ? 'Please type DELETE exactly' : ''}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleDeleteDialogClose} 
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAccountConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting || deleteConfirmText !== 'DELETE'}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteForeverIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete My Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
